import { api, type ApiRequestConfig } from 'api/v1/api';
import { studyScheduleCodes, studyTimeFromApi, studyTimeToApi } from 'utils/studyTime';

const COOKIE = 'mt_podbor_draft';
const VERIFICATION_BROWSER_KEY_COOKIE = 'mt_verification_browser_key';

export type PodborDraft = {
  studentType?: string;
  grades?: string[];
  subject?: string;
  subjectLabel?: string;
  goal?: string;
  goalLabel?: string;
  teacherGender?: string;
  duration?: string;
  locations?: string[];
  schedule?: string[];
  /** Структурное время из подбора/плана (Figma шаг 6). */
  scheduleTime?: { mode?: string; days?: string[]; time_from?: string | null; time_to?: string | null; slots?: Array<{ day: string; time_from: string; time_to: string }> } | null;
  comment?: string;
  priceFrom?: number | null;
  priceTo?: number | null;
  trial?: string;
  studentName?: string;
  registrantType?: 'self' | 'parent';
};

const LOCATION_MAP: Record<string, string> = {
  Онлайн: 'online',
  'У репетитора': 'at_tutor',
  'У меня': 'at_student',
  Неважно: 'any',
};

function cookieDomain(): string {
  if (typeof window === 'undefined') return '';
  const host = window.location.hostname;
  if (host.endsWith('.myteacher.pro')) return '; Domain=.myteacher.pro';
  return '';
}

function readNamedCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  if (!match) return null;
  return decodeURIComponent(match.slice(name.length + 1));
}

function writeNamedCookie(name: string, value: string, maxAge: number): void {
  if (typeof document === 'undefined') return;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax${cookieDomain()}${secure}`;
}

function clearNamedCookie(name: string): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=; Path=/; Max-Age=0${cookieDomain()}`;
}

function readCookie(): string | null {
  return readNamedCookie(COOKIE);
}

export function saveVerificationBrowserKey(key: string): void {
  if (!key) return;
  writeNamedCookie(VERIFICATION_BROWSER_KEY_COOKIE, key, 48 * 3600);
  try {
    localStorage.setItem('verification_browser_key', key);
  } catch {
    /* ignore */
  }
}

export function readVerificationBrowserKey(): string | null {
  try {
    const fromLs = localStorage.getItem('verification_browser_key');
    if (fromLs) return fromLs;
  } catch {
    /* ignore */
  }
  return readNamedCookie(VERIFICATION_BROWSER_KEY_COOKIE);
}

export function clearVerificationBrowserKey(): void {
  clearNamedCookie(VERIFICATION_BROWSER_KEY_COOKIE);
  try {
    localStorage.removeItem('verification_browser_key');
  } catch {
    /* ignore */
  }
}

export function readPodborDraft(): PodborDraft | null {
  try {
    const raw = readCookie();
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PodborDraft;
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearPodborDraft(): void {
  clearNamedCookie(COOKIE);
}

export function mapPodborDraftToBulletin(
  draft: PodborDraft,
  profile?: { registrant_type?: string; name?: string; surname?: string },
) {
  const registrantType = draft.registrantType || profile?.registrant_type || 'self';
  const isSelf = registrantType !== 'parent';
  const grades =
    draft.studentType === 'school'
      ? (draft.grades || []).map((g) => Number(g)).filter((n) => Number.isFinite(n) && n > 0)
      : [];
  const studentName =
    (isSelf ? [profile?.name, profile?.surname].filter(Boolean).join(' ') : draft.studentName || '').trim();

  const payload: Record<string, unknown> = {
    student_type: draft.studentType || 'graduated',
    grades,
    grade: grades[0],
    participants: [],
    subject_category: draft.subject || 'matematika',
    student_name: studentName,
    self_study: isSelf,
    tutor_gender_preference: draft.teacherGender || 'any',
    planned_duration: draft.duration || 'later',
    wishes: draft.comment ? String(draft.comment).slice(0, 2000) : undefined,
    price_from: draft.priceFrom ? Number(draft.priceFrom) : undefined,
    price_to: draft.priceTo ? Number(draft.priceTo) : undefined,
    trial_lesson_preference: draft.trial || 'yes',
  };
  if (draft.goal) payload.subject_subcategory = draft.goal;
  if (draft.locations?.length) {
    payload.study_locations = draft.locations.map((loc) => LOCATION_MAP[loc]).filter(Boolean);
  }
  if (draft.schedule?.length) payload.study_schedule = [...draft.schedule];
  const studyTime = studyTimeToApi(studyTimeFromApi(draft.scheduleTime, draft.schedule));
  if (studyTime) {
    payload.study_time = studyTime;
    if (!draft.schedule?.length) payload.study_schedule = studyScheduleCodes(studyTime);
  }
  return payload;
}

export function draftToTaskFormValues(draft: PodborDraft): Record<string, unknown> {
  return {
    studentType: draft.studentType || 'graduated',
    grades: draft.grades || [],
    subject: draft.subject || 'matematika',
    subjectLabel: draft.subjectLabel || 'Математика',
    goal: draft.goal || '',
    goalLabel: draft.goalLabel || '',
    teacherGender: draft.teacherGender || 'any',
    duration: draft.duration || '',
    locations: draft.locations || [],
    studyTime: studyTimeFromApi(draft.scheduleTime, draft.schedule),
    comment: draft.comment || '',
    priceFrom: draft.priceFrom ?? 500,
    priceTo: draft.priceTo ?? null,
    trial: draft.trial || '',
    studentName: draft.studentName || '',
    isSelf: draft.registrantType !== 'parent',
  };
}

export type ServerPodborDraftPeek = {
  exists?: boolean;
  consumed?: boolean;
  is_parent?: boolean;
  payload?: PodborDraft | null;
};

export type ServerPodborDraftConsume = {
  status?: string;
  bulletin_id?: number | null;
  is_parent?: boolean;
  payload?: PodborDraft | null;
};

export async function fetchServerPodborDraft(): Promise<ServerPodborDraftPeek> {
  const { data } = await api.get<ServerPodborDraftPeek>('/podbor-draft/', {
    skipGlobalErrorHandler: true,
  } as ApiRequestConfig);
  return data || { exists: false };
}

export async function consumeServerPodborDraft(): Promise<ServerPodborDraftConsume> {
  const { data } = await api.post<ServerPodborDraftConsume>(
    '/podbor-draft/consume/',
    {},
    { skipGlobalErrorHandler: true } as ApiRequestConfig,
  );
  return data || { status: 'none' };
}
